import { getServerOrigin } from "../storage/index.js";
import { t } from "../../shared/i18n/index.js";

export function getCsrfTokenFromCookie(cookie = globalThis.document?.cookie || "") {
  const match = cookie.split(";").map(part => part.trim()).find(part => part.startsWith("csrf_token="));
  return match?.slice("csrf_token=".length) || null;
}

// Waiting callers can cancel independently without aborting a shared lookup.
function waitWithSignal(promise, signal) {
  if (!signal) return promise;
  return new Promise((resolve, reject) => {
    const aborted = () => reject(signal.reason);
    if (signal.aborted) aborted();
    else signal.addEventListener("abort", aborted, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", aborted));
  });
}

// Never cache a cookie across login/logout or send it to another service.
export function createCsrfFetch(original, {
  pageUrl, serverOrigin = null, getCookie = getCsrfTokenFromCookie, tokenTimeoutMs = 10000,
}) {
  const page = new URL(pageUrl);
  const sameOrigin = (a, b) => a.protocol === b.protocol && a.host === b.host;
  const server = serverOrigin ? new URL(serverOrigin) : null;
  let pendingToken = null;
  const remoteToken = () => {
    if (!pendingToken) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(
        new DOMException(t("account.error.csrfTimeout"), "TimeoutError"),
      ), tokenTimeoutMs);
      const lookup = (async () => {
        const response = await original(`${serverOrigin}/api/auth/csrf`, {
          credentials: "include", cache: "no-store", signal: controller.signal,
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        if (!response.ok) throw new Error(t("account.error.csrfUnsupported", { status: String(response.status) }));
        const data = await response.json();
        if (data.csrf_token !== null && typeof data.csrf_token !== "string") {
          throw new Error("Invalid desktop CSRF response");
        }
        return data.csrf_token;
      })();
      // Bound response-body reading too, even if the transport ignores abort.
      pendingToken = waitWithSignal(lookup, controller.signal).finally(() => {
        clearTimeout(timer);
        pendingToken = null;
      });
    }
    return pendingToken;
  };
  return async function csrfFetch(input, init) {
    const request = typeof Request !== "undefined" && input instanceof Request ? input : null;
    const method = (init?.method || request?.method || "GET").toUpperCase();
    if (!["POST", "PUT", "PATCH", "DELETE"].includes(method)) return original(input, init);
    const target = new URL(request?.url || String(input), page);
    const isPage = sameOrigin(target, page);
    const isServer = server && sameOrigin(target, server);
    if (!isPage && !isServer) return original(input, init);
    // Tauri IPC and other native protocols never receive credentials.
    if (isPage && server && !/^\/(api|auth|e2ee|health|users|keypair|shares|invitations|records)(\/|$)/.test(target.pathname)) {
      return original(input, init);
    }
    const signal = init?.signal !== undefined ? init.signal : request?.signal;
    signal?.throwIfAborted();
    const credentials = init?.credentials ?? request?.credentials ?? "same-origin";
    if (credentials === "omit" || (server && !sameOrigin(page, server) && credentials !== "include")) {
      return original(input, init);
    }
    const headers = new Headers(init?.headers ?? request?.headers);
    if (headers.has("X-CSRF-Token")) return original(input, init);
    const token = server ? await waitWithSignal(remoteToken(), signal) : getCookie();
    signal?.throwIfAborted();
    if (token) headers.set("X-CSRF-Token", token);
    return original(input, { ...init, headers });
  };
}

let installed = false;
export function installCsrfInjector() {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  window.fetch = createCsrfFetch(window.fetch.bind(window), {
    pageUrl: window.location.href,
    serverOrigin: getServerOrigin?.() || null,
  });
}
